import { useBakeGroup } from '@/composables/useBakeGroup';
import { usePdfExport } from '@/composables/usePdfExport';
import {
  MAX_SCALE,
  MAX_SCALE_X,
  MAX_SCALE_Y,
  MAX_SCALE_Z,
  MIN_SCALE,
  MIN_SCALE_X,
  MIN_SCALE_Y,
  MIN_SCALE_Z,
} from '@/constants/appConstant';
import { CUBE, CYLINDER, PLANE, SPHERE, TORUS_KNOT } from '@/constants/object';
import { changeTransformControls } from '@/threejs/handlers/transform';
import {
  Ban,
  Box,
  Boxes,
  Camera,
  ChartColumn,
  Cylinder,
  FileUp,
  Folder,
  Globe,
  Grid3x3,
  ListTree,
  MessageCircle,
  Monitor,
  MonitorPlay,
  PencilRuler,
  Projector,
  Ruler,
  Shapes,
  Tag,
  Zap,
} from 'lucide-vue-next';
import * as THREE from 'three';

export const settingPanels = {
  default: [
    {
      label: 'Name',
      key: 'name',
      type: 'text',
      root: true, // Access root level instead of data
    },

    {
      label: 'Parent',
      key: 'parentCode',
      type: 'parent',
    },

    {
      label: 'Position',
      sublabel: 'X',
      key: 'position.x',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'x', true);
      },
    },
    {
      key: 'position.y',
      sublabel: 'Y',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'y', true);
      },
    },
    {
      key: 'position.z',
      sublabel: 'Z',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'z');
      },
    },

    {
      label: 'Rotation',
      key: 'rotation.x',
      sublabel: 'X',
      type: 'angle',
      default: 0,
      focus: () => {
        changeTransformControls('rotate', 'x', true);
      },
    },
    {
      key: 'rotation.y',
      sublabel: 'Y',
      type: 'angle',
      default: 0,
      focus: () => {
        changeTransformControls('rotate', 'y', true);
      },
    },
    {
      key: 'rotation.z',
      sublabel: 'Z',
      type: 'angle',
      default: 0,
      focus: () => {
        changeTransformControls('rotate', 'z', true);
      },
    },

    {
      label: 'Scale',
      sublabel: 'X',
      min: MIN_SCALE_X,
      max: MAX_SCALE_X,
      step: 0.01,
      key: 'scale.x',
      type: 'number',
      default: 1,
      focus: () => {
        changeTransformControls('scale', 'x', true);
      },
      visible: (context) => context['lock_scale'] === false,
    },
    {
      key: 'scale.y',
      sublabel: 'Y',
      min: MIN_SCALE_Y,
      max: MAX_SCALE_Y,
      step: 0.01,
      type: 'number',
      default: 1,
      focus: () => {
        changeTransformControls('scale', 'y', true);
      },
      visible: (context) => context['lock_scale'] === false,
    },
    {
      key: 'scale.z',
      sublabel: 'Z',
      type: 'number',
      min: MIN_SCALE_Z,
      max: MAX_SCALE_Z,
      step: 0.01,
      default: 1,
      focus: () => {
        changeTransformControls('scale', 'z', true);
      },
      visible: (context) => context['lock_scale'] === false,
    },
    {
      key: 'scale',
      sublabel: '',
      label: 'Scale',
      type: 'number',
      min: MIN_SCALE,
      max: MAX_SCALE,
      step: 0.01,
      default: 1,
      focus: () => {
        changeTransformControls('scale', '', true);
      },
      visible: (context) => context['lock_scale'] !== false,
    },
    {
      label: 'Lock Scale',
      key: 'lock_scale',
      type: 'boolean',
      default: true,
    },

    {
      label: 'Visible',
      key: 'visible',
      type: 'boolean',
      default: true,
    },
    {
      label: 'Lock',
      key: 'lock',
      type: 'boolean',
      default: false,
    },
  ],
  material: [
    {
      label: 'Material',
      key: 'material',
      type: 'radio',
      default: 'color',
      options: [
        { label: 'Colour', value: 'color', type: 'color' },
        { label: 'Base Map', value: 'map', type: 'asset' },
      ],
    },
    {
      label: 'Asset',
      key: 'resource_id',
      type: 'asset',
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Colour',
      key: 'color',
      type: 'color',
      default: '#ffffff',
    },
    {
      label: 'Luminance (cd/m2)',
      key: 'luminance',
      type: 'number',
      default: 0,
      min: 0,
      max: 100,
    },
    { label: 'Gain', key: 'gain', type: 'number', default: 0.5, min: 0, max: 1, step: 0.01 },
    { label: 'Wireframe', key: 'wireframe', type: 'boolean', default: false },

    {
      label: 'Map Repeat X',
      key: 'repeatX',
      type: 'number',
      default: 1,
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Map Repeat Y',
      key: 'repeatY',
      type: 'number',
      default: 1,
      visible: (context) => context['material'] === 'map',
    },

    {
      label: 'Map Offset X',
      key: 'offsetX',
      type: 'number',
      default: 0,
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Map Offset Y',
      key: 'offsetY',
      type: 'number',
      default: 0,
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Map Wrapping X',
      key: 'wrappingX',
      type: 'select',
      options: [
        { label: 'Repeat', value: 'repeat' },
        { label: 'Normal', value: 'normal' },
        { label: 'Mirror', value: 'mirror' },
      ],
      default: 'repeat',
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Map Wrapping Y',
      key: 'wrappingY',
      type: 'select',
      options: [
        { label: 'Repeat', value: 'repeat' },
        { label: 'Normal', value: 'normal' },
        { label: 'Mirror', value: 'mirror' },
      ],
      default: 'repeat',
      visible: (context) => context['material'] === 'map',
    },
    {
      label: 'Side',
      key: 'side',
      type: 'radio',
      options: [
        { label: 'Front', value: THREE.FrontSide },
        { label: 'Back', value: THREE.BackSide },
        { label: 'Double', value: THREE.DoubleSide },
      ],
      default: THREE.FrontSide,
    },
  ],

  box: [
    { sections: [{ type: 'default' }, { type: 'material' }] },
    {
      columns: [
        { label: 'Width', key: 'width', type: 'number', min: 1 },
        { label: 'Depth', key: 'depth', type: 'number', min: 1 },
        { label: 'Height', key: 'height', type: 'number', min: 1 },
      ],
    },
    {
      columns: [
        {
          label: 'Width Segments',
          key: 'widthSegments',
          type: 'number',
          min: 1,
          default: 1,
        },
        {
          label: 'Depth Segments',
          key: 'depthSegments',
          type: 'number',
          min: 1,
          default: 1,
        },
        {
          label: 'Height Segments',
          key: 'heightSegments',
          type: 'number',
          min: 1,
          default: 1,
        },
      ],
    },
  ],
  cube: [
    { sections: [{ type: 'default' }, { type: 'material' }] },
    {
      columns: [
        { label: 'Width ', key: 'width', type: 'number', default: CUBE.WIDTH, min: 0.01 },
        { label: 'Depth', key: 'depth', type: 'number', default: CUBE.DEPTH, min: 0.01 },
        { label: 'Height', key: 'height', type: 'number', default: CUBE.HEIGHT, min: 0.01 },
      ],
    },
    {
      columns: [
        {
          label: 'Width Segments',
          key: 'widthSegments',
          type: 'number',
          default: 1,
          min: 1,
        },
        {
          label: 'Depth Segments',
          key: 'depthSegments',
          type: 'number',
          default: 1,
          min: 1,
        },
        {
          label: 'Height Segments',
          key: 'heightSegments',
          type: 'number',
          default: 1,
          min: 1,
        },
      ],
    },
  ],
  plane: [
    {
      sections: [
        {
          type: 'default',
          exclude: ['scale.x', 'scale.y', 'scale.z', 'scale', 'lock_scale'],
        },
        { type: 'material' },
      ],
    },
    {
      columns: [
        { label: 'Width', key: 'width', type: 'number', default: PLANE.WIDTH, min: 0.01 },
        { label: 'Height', key: 'height', type: 'number', default: PLANE.HEIGHT, min: 0.01 },
      ],
    },
  ],

  sphere: [
    { sections: [{ type: 'default' }, { type: 'material' }] },
    { label: 'Radius', key: 'radius', type: 'number', min: 0.01, default: SPHERE.RADIUS },
    {
      label: 'Segments',
      columns: [
        {
          label: 'Width ',
          key: 'widthSegments',
          type: 'number',
          min: 3,
          default: SPHERE.WIDTH_SEGMENTS,
        },
        {
          label: 'Depth ',
          key: 'heightSegments',
          type: 'number',
          min: 3,
          default: SPHERE.HEIGHT_SEGMENTS,
        },
      ],
    },

    {
      label: 'Phi',
      columns: [
        { label: 'Start', key: 'phiStart', type: 'number', default: SPHERE.PHI_START, min: 0 },
        {
          label: 'Length',
          key: 'phiLength',
          type: 'number',
          default: SPHERE.PHI_LENGTH,
          min: 0,
          max: 360,
        },
      ],
    },
    {
      label: 'Theta',
      columns: [
        {
          label: 'Start',
          key: 'thetaStart',
          type: 'number',
          default: SPHERE.THETA_START,
          min: 0,
        },
        {
          label: 'Length',
          key: 'thetaLength',
          type: 'number',
          default: SPHERE.THETA_LENGTH,
          min: 0,
          max: 180,
        },
      ],
    },
  ],
  torusknot: [
    { sections: [{ type: 'default' }, { type: 'material' }] },

    {
      label: 'Radius',
      key: 'radius',
      type: 'number',
      min: 0.01,
      default: TORUS_KNOT.RADIUS,
    },

    {
      label: 'Radial Segments',
      key: 'radialSegments',
      type: 'number',
      min: 3,
      default: TORUS_KNOT.RADIAL_SEGMENTS,
      max: 128,
    },

    {
      label: 'Tubular Segments',
      key: 'tubularSegments',
      type: 'number',
      min: 32,
      default: TORUS_KNOT.TUBULAR_SEGMENTS,
      max: 128,
    },

    {
      label: 'P Start',
      key: 'pStart',
      type: 'number',
      min: 0,
      default: 2,
    },

    {
      label: 'P Length',
      key: 'pLength',
      type: 'number',
      min: 0,
      default: 1,
    },

    {
      label: 'Q Start',
      key: 'qStart',
      type: 'number',
      min: 0,
      default: 3,
    },

    {
      label: 'Q Length',
      key: 'qLength',
      type: 'number',
      min: 0,
      default: 1,
    },
  ],

  cylinder: [
    {
      sections: [{ type: 'default' }, { type: 'material' }],
    },
    {
      label: 'Radius Top',
      key: 'radiusTop',
      type: 'number',
      default: CYLINDER.RADIUS_TOP,
      min: 0.01,
    },
    {
      label: 'Radius Bottom',
      key: 'radiusBottom',
      type: 'number',
      default: CYLINDER.RADIUS_BOTTOM,
      min: 0.01,
    },
    {
      label: 'Height',
      key: 'height',
      type: 'number',
      default: CYLINDER.HEIGHT,
      min: 0.01,
    },
    {
      label: 'Segments',
      columns: [
        {
          label: 'Radial Segments',
          key: 'radialSegments',
          type: 'number',
          default: CYLINDER.RADIAL_SEGMENTS,
          min: 3,
        },
        {
          label: 'Height Segments',
          key: 'heightSegments',
          type: 'number',
          default: 1,
          min: 1,
        },
      ],
    },
  ],

  group: [
    { sections: [{ type: 'default' }] },
    {
      label: 'Mode',
      key: 'groupMode',
      type: 'radio',
      options: [
        { label: 'Normal', value: 'normal' },
        { label: 'Symmetry', value: 'symmetry' },
        { label: 'Linear Array', value: 'linear_array' },
        { label: 'Polar Array', value: 'polar_array' },
      ],
      default: 'normal',
    },
    {
      label: 'Symmetry Plane',
      key: 'symmetryPlane',
      type: 'radio',
      options: [
        { label: 'X-Y Plane', value: 'xy' },
        { label: 'X-Z Plane', value: 'xz' },
        { label: 'Y-Z Plane', value: 'yz' },
      ],
      default: 'yz',
      visible: (context) => context['groupMode'] === 'symmetry',
    },
    {
      label: 'Symmetry Offset',
      key: 'symmetryOffset',
      type: 'number',
      default: 0,
      step: 0.1,
      visible: (context) => context['groupMode'] === 'symmetry',
    },
    {
      label: 'Array Count',
      key: 'arrayCount',
      type: 'number',
      default: 3,
      min: 1,
      max: 50,
      step: 1,
      visible: (context) => context['groupMode'] === 'linear_array',
    },
    {
      label: 'Array Translation',
      key: 'arrayTranslation',
      type: 'column',
      columns: [
        {
          key: 'arrayTranslation.x',
          sublabel: 'X',
          type: 'number',
          default: 2,
          step: 0.1,
        },
        {
          key: 'arrayTranslation.y',
          sublabel: 'Y',
          type: 'number',
          default: 0,
          step: 0.1,
        },
        {
          key: 'arrayTranslation.z',
          sublabel: 'Z',
          type: 'number',
          default: 0,
          step: 0.1,
        },
      ],
      visible: (context) => context['groupMode'] === 'linear_array',
    },
    {
      label: 'Array Rotation',
      key: 'arrayRotation',
      type: 'column',
      columns: [
        {
          key: 'arrayRotation.x',
          sublabel: 'X',
          type: 'number',
          default: 0,
          step: 0.1,
        },
        {
          key: 'arrayRotation.y',
          sublabel: 'Y',
          type: 'number',
          default: 0,
          step: 0.1,
        },
        {
          key: 'arrayRotation.z',
          sublabel: 'Z',
          type: 'number',
          default: 0,
          step: 0.1,
        },
      ],
      visible: (context) => context['groupMode'] === 'linear_array',
    },
    {
      label: 'Polar Count',
      key: 'polarCount',
      type: 'number',
      default: 6,
      min: 1,
      max: 50,
      step: 1,
      visible: (context) => context['groupMode'] === 'polar_array',
    },
    {
      label: 'Polar Angle',
      key: 'polarAngle',
      type: 'number',
      default: 90,
      min: 0,
      max: 360,
      step: 1,
      visible: (context) => context['groupMode'] === 'polar_array' && !context['polarFullOverride'],
    },
    {
      label: 'Full 360',
      key: 'polarFullOverride',
      type: 'boolean',
      default: false,
      visible: (context) => context['groupMode'] === 'polar_array',
    },
    {
      label: 'Polar Axis',
      key: 'polarAxis',
      type: 'radio',
      options: [
        { label: 'X', value: 'x' },
        { label: 'Y', value: 'y' },
        { label: 'Z', value: 'z' },
      ],
      default: 'y',
      visible: (context) => context['groupMode'] === 'polar_array',
    },
    {
      label: '',
      type: 'button',
      buttonText: 'Bake Items',
      disabled: (context) =>
        context['groupMode'] === 'linear_array' ||
        context['groupMode'] === 'symmetry' ||
        context['groupMode'] === 'polar_array',
      onClick: () => {
        const { bakeGroupItems } = useBakeGroup();
        bakeGroupItems();
      },
    },
  ],

  model: [
    { sections: [{ type: 'default' }] },
    {
      columns: [{ label: 'Double Sided', key: 'doubleSided', type: 'boolean' }],
    },
  ],

  mesh: [
    {
      label: 'Name',
      key: 'name',
      type: 'text',
      root: true, // Access root level instead of data
    },
    {
      label: 'Visible',
      key: 'visible',
      type: 'boolean',
      default: true,
    },
    {
      columns: [{ label: 'UVs', key: 'listUVs', type: 'listuvs' }],
    },
  ],

  led_screen: [
    {
      sections: [
        {
          type: 'default',
          exclude: ['scale.x', 'scale.y', 'scale.z', 'scale', 'lock_scale'],
        },
      ],
    },

    {
      label: 'Panel Library',
      key: 'libraryCode',
      type: 'library',
      libraryType: 'led',
    },

    { label: 'LED Stats', key: 'ledStats', type: 'ledstats' },

    {
      label: 'Size Mode',
      key: 'sizeMode',
      type: 'radio',
      options: [
        { label: 'Tile Count', value: 'tiles' },
        { label: 'Dimensions', value: 'dimensions' },
      ],
      default: 'tiles',
    },

    {
      columns: [
        {
          label: 'H',
          key: 'horizontalSize',
          type: 'number',
          step: 1,
          min: 1,
          default: 16,
        },
        {
          label: 'V',
          key: 'verticalSize',
          type: 'number',
          step: 1,
          min: 1,
          default: 9,
        },
      ],
      label: 'Tiles',
      visible: (context) => context['sizeMode'] !== 'dimensions',
    },

    {
      columns: [
        {
          label: 'W',
          key: 'totalWidth',
          type: 'measurement',
          step: 0.1,
          min: 0.1,
        },
        {
          label: 'H',
          key: 'totalHeight',
          type: 'measurement',
          step: 0.1,
          min: 0.1,
        },
      ],
      label: 'Dimensions',
      visible: (context) => context['sizeMode'] === 'dimensions',
    },

    {
      label: 'Round Tiles',
      key: 'roundTiles',
      type: 'radio',
      options: [
        { label: 'Up', value: 'up' },
        { label: 'Down', value: 'down' },
      ],
      default: 'down',
      visible: (context) => context['sizeMode'] === 'dimensions',
    },
    {
      columns: [
        {
          label: 'H',
          key: 'horizontalCurvature',
          type: 'angle',
          step: 1,
          default: 0,
        },
        {
          label: 'V',
          key: 'verticalCurvature',
          type: 'angle',
          step: 1,
          default: 0,
        },
      ],
      label: 'Curvature',
    },
    {
      label: 'Gain',
      key: 'gain',
      type: 'number',
      default: 0.5,
      min: 0,
      max: 1,
      step: 0.01,
    },
    {
      label: 'Test Pattern',
      key: 'inputSource',
      type: 'pattern-picker',
      sources: ['white', 'grid1', 'grid1inverted', 'grid2', 'colorbars', 'crosshatch', 'testCard', 'none', 'source'],
      default: 'grid1',
    },
    {
      label: 'Source',
      key: 'source',
      type: 'asset',
      visible: (context) => context['inputSource'] === 'source',
    },

    {
      label: 'Power',
      key: 'power',
      type: 'boolean',
      default: true,
    },
    {
      label: 'UV Scale',
      columns: [
        {
          label: 'X',
          key: 'uvScaleX',
          type: 'number',
          default: 1,
          step: 0.1,
        },
        {
          label: 'Y',
          key: 'uvScaleY',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
    },
    {
      label: 'UV Offset',
      columns: [
        {
          label: 'X',
          key: 'uvOffsetX',
          type: 'number',
          default: 0,
          step: 0.1,
        },
        {
          label: 'Y',
          key: 'uvOffsetY',
          type: 'number',
          default: 0,
          step: 0.1,
        },
      ],
    },
  ],

  projector: [
    {
      sections: [
        {
          type: 'default',
          exclude: ['scale.x', 'scale.y', 'scale.z', 'scale', 'lock_scale'],
        },
      ],
    },
    // Targeting and Positioning
    {
      label: 'Lock Target',
      key: 'lockTarget',
      type: 'boolean',
      default: false,
    },
    {
      label: 'Target',
      sublabel: 'X',
      key: 'target.x',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'x', true, true);
      },
    },
    {
      sublabel: 'Y',
      key: 'target.y',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'y', true, true);
      },
    },
    {
      sublabel: 'Z',
      key: 'target.z',
      type: 'measurement',
      default: 0,
      focus: () => {
        changeTransformControls('translate', 'z', true, true);
      },
    },
    {
      label: 'Projector Library',
      key: 'libraryCode',
      type: 'library',
      libraryType: 'projector',
    },

    {
      label: 'Projector Stats',
      key: 'projectorStats',
      type: 'projectorstats',
      //visible: (context) => context['libraryCode'] !== null
    },

    {
      label: 'Lumens',
      key: 'lumens',
      type: 'number',
      step: 100,
      min: 1000,
      max: 50000,
      default: 20000,
      // visible: (context) => context['libraryCode'] === null,
    },

    {
      label: 'Throw Ratio',
      key: 'throwRatio',
      type: 'ratio',
      step: 0.1,
      min: 0.2,
      max: 6,
      default: 1.4,
    },
    {
      label: 'Gain',
      key: 'gain',
      type: 'number',
      default: 0.5,
      min: 0,
      max: 1,
      step: 0.01,
    },

    {
      label: 'Lens Shift',
      key: 'lensShift',
      type: 'column',
      columns: [
        {
          label: 'H(%)',
          key: 'lensShiftHorizontal',
          type: 'number',
          default: 0,
          min: -100,
          max: 100,
          step: 1,
        },
        {
          label: 'V(%)',
          key: 'lensShiftVertical',
          type: 'number',
          default: 0,
          min: -100,
          max: 100,
          step: 1,
        },
      ],
    },
    {
      label: 'Stacked Projectors',
      key: 'stackedProjectors',
      type: 'number',
      step: 1,
      default: 1,
      min: 1,
      max: 10,
    },

    {
      label: 'Stack Offset',
      sublabel: 'X',
      key: 'stackOffset.x',
      type: 'measurement',
      default: 0,
      visible: (context) => (context['stackedProjectors'] || 1) > 1,
    },
    {
      key: 'stackOffset.y',
      sublabel: 'Y',
      type: 'measurement',
      default: 0.35,
      visible: (context) => (context['stackedProjectors'] || 1) > 1,
    },
    {
      key: 'stackOffset.z',
      sublabel: 'Z',
      type: 'measurement',
      default: 0,
      visible: (context) => (context['stackedProjectors'] || 1) > 1,
    },

    {
      label: 'Power Brightness (%)',
      key: 'power_brightness',
      type: 'slider',
      step: 1,
      min: 0,
      max: 100,
      default: 100,
    },

    {
      label: 'Power',
      key: 'power',
      type: 'boolean',
      default: true,
    },

    // Test Patterns and Content
    {
      label: 'Test Pattern',
      key: 'inputSource',
      type: 'pattern-picker',
      sources: ['solid', 'grid1', 'grid1inverted', 'grid2', 'colorbars', 'crosshatch', 'testCard', 'none', 'source'],
      default: 'grid1',
    },

    {
      label: 'Solid Colour',
      key: 'solidColor',
      type: 'color',
      default: '#ffffff',
      visible: (context) => context['inputSource'] === 'solid',
    },
    {
      label: 'Source',
      key: 'source',
      type: 'asset',
      visible: (context) => context['inputSource'] === 'source',
    },

    {
      label: 'Frustum Edge Colour',
      key: 'frustumEdgeColor',
      type: 'color',
      default: '#0000ff',
    },
  ],
  brightness: [
    {
      group: 'viewport_settings',
      label: 'Directional Light',
      key: 'brightness.directionalLight',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      disabled: (context) => context['brightness.directionalLight'] === true,
      label: 'Directional Light',
      key: 'brightness.directionalLightIntensity',
      step: 0.05,
      type: 'light',
      default: 0.05,
      min: 0,
      max: 1,
    },
    {
      group: 'viewport_settings',
      disabled: (context) => context['brightness.directionalLight'] === true,
      label: 'Yaw',
      key: 'brightness.directionalLightYaw',
      type: 'angle',
      default: 0,
      min: -180,
      max: 180,
      step: 1,
    },
    {
      group: 'viewport_settings',
      disabled: (context) => context['brightness.directionalLight'] === true,
      label: 'Pitch',
      key: 'brightness.directionalLightPitch',
      type: 'angle',
      default: 45,
      min: -90,
      max: 90,
      step: 1,
    },
    {
      group: 'viewport_settings',
      label: 'Ambient Light',
      key: 'brightness.ambientLight',
      type: 'boolean',
      default: true,
    },
    {
      group: 'viewport_settings',
      disabled: (context) => context['brightness.ambientLight'] === true,
      label: 'Lighting',
      key: 'brightness.ambientLightIntensity',
      step: 0.05,
      type: 'light',
      default: 0.03,
      min: 0,
      max: 1,
    },
  ],

  grid: [
    {
      group: 'viewport_settings',
      label: 'Show Grid',
      key: 'grid.enabled',
      type: 'boolean',
      default: true,
    },
    {
      group: 'viewport_settings',
      label: 'Size',
      key: 'grid.size',
      type: 'number',
      default: 10,
      disabled: (context) => context['grid.enabled'] === true,
    },
    {
      group: 'viewport_settings',
      label: 'Sub sections',
      key: 'grid.subsections',
      type: 'number',
      default: 10,
      disabled: (context) => context['grid.enabled'] === true,
    },
    {
      group: 'viewport_settings',
      label: 'Grid Colour',
      key: 'grid.color2',
      type: 'color',
      default: '#374151',
      disabled: (context) => context['grid.enabled'] === true,
    },
    {
      group: 'viewport_settings',
      label: 'Sub Grid Colour',
      key: 'grid.color1',
      type: 'color',
      default: '#1F2937',
      disabled: (context) => context['grid.enabled'] === true,
    },
  ],

  reports: [
    {
      group: 'viewport_settings',
      label: 'View',
      key: 'render.view',
      type: 'radio',
      default: 'default',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Default', value: 'default' },
        { label: 'Photometric', value: 'photometric' },
        { label: 'Pixel Size', value: 'pixelSize' },
      ],
    },
    {
      group: 'viewport_settings',
      label: 'Show Overlaps',
      key: 'render.overlaps',
      type: 'boolean',
      default: false,
    },

    {
      group: 'viewport_settings',
      label: 'Range',
      key: 'render.range',
      type: 'photometricstats',
      visible: (context) => context['render.view'] === 'photometric',
    },
    {
      group: 'viewport_settings',
      label: 'Stepped Spectrum Colours',
      key: 'render.photometricSteppedColors',
      type: 'boolean',
      default: false,
      visible: (context) => context['render.view'] === 'photometric',
    },
    {
      group: 'viewport_settings',
      label: 'Maximum Value (lx)',
      key: 'render.photometricMaxValue',
      type: 'number',
      default: 2000,
      min: 100,
      max: 20000,
      step: 100,
      visible: (context) => context['render.view'] === 'photometric',
    },

    {
      group: 'viewport_settings',
      label: 'Ideal Pixel Size (mm)',
      key: 'render.idealPixelSize',
      type: 'number',
      default: 4,
      min: 0.5,
      max: 20,
      step: 0.5,
      visible: (context) => context['render.view'] === 'pixelSize',
    },

    { type: 'separator' },
    {
      label: 'Export Project Items',
      type: 'button',
      buttonText: 'Download PDF Item Report',
      onClick: () => {
        const { generateProjectReport } = usePdfExport();
        generateProjectReport();
      },
    },
  ],
  project_settings: [
    {
      label: 'Measurement Units',
      key: 'units.length',
      type: 'select',
      options: [
        { label: 'Meters (m)', value: 'meters' },
        { label: 'Centimeters (cm)', value: 'centimeters' },
        { label: 'Millimeters (mm)', value: 'millimeters' },
        { label: 'Feet (ft)', value: 'feet' },
        { label: 'Inches (in)', value: 'inches' },
      ],
      default: 'meters',
    },
    {
      label: 'Angular Units',
      key: 'units.angle',
      type: 'select',
      options: [
        { label: 'Degrees (°)', value: 'degrees' },
        { label: 'Radians (rad)', value: 'radians' },
      ],
      default: 'degrees',
    },
    {
      label: 'Photometric Units',
      key: 'units.photometric',
      type: 'select',
      options: [
        { label: 'Lux/Lumens', value: 'metric' },
        { label: 'Foot-candles/Foot-lamberts', value: 'imperial' },
      ],
      default: 'metric',
    },
    {
      label: 'Mouse Controls',
      key: 'mouse.controls',
      type: 'radio',
      options: [
        { label: 'Left Rotate', value: 'rotate' },
        { label: 'Left Truck', value: 'truck' },
      ],
      default: 'truck',
    },
    {
      label: 'Timeline Duration',
      key: 'timeline.duration',
      type: 'number',
      default: 600,
      min: 1,
      max: 36000,
      step: 1,
      suffix: 's',
    },
    {
      label: 'Timeline Display Unit',
      key: 'timeline.timeUnit',
      type: 'select',
      options: [
        { label: 'Seconds', value: 'seconds' },
        { label: 'Frames', value: 'frames' },
      ],
      default: 'seconds',
    },
    {
      label: 'Timeline FPS',
      key: 'timeline.fps',
      type: 'number',
      default: 30,
      min: 1,
      max: 120,
      step: 1,
      suffix: 'fps',
    },
  ],
  render: [
    {
      group: 'viewport_settings',
      label: 'Show Projector Info',
      key: 'render.showProjectorInfo',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Show Frustum Edges',
      key: 'render.frustumEdges',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Show Projector Sprites',
      key: 'render.showProjectorSprite',
      type: 'boolean',
      default: false,
    },
    { type: 'separator' },
    {
      group: 'viewport_settings',
      label: 'Background',
      key: 'render.background',
      default: '#111828',
      type: 'color',
    },
    {
      group: 'viewport_settings',
      label: 'Gradient',
      key: 'render.gradient',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Background 2',
      key: 'render.background2',
      default: '#000000',
      type: 'color',
      disabled: (context) => context['render.gradient'] === true,
    },
    { type: 'separator' },
    {
      group: 'viewport_settings',
      label: 'Show Object Edges',
      key: 'render.showObjectEdges',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Object Edge Colour',
      key: 'render.objectEdgeColour',
      type: 'color',
      default: '#ffffff',
      disabled: (context) => context['render.showObjectEdges'] === true,
    },
    {
      group: 'viewport_settings',
      label: 'Object Edge Thickness',
      key: 'render.objectEdgeThickness',
      type: 'number',
      min: 0.1,
      default: 1,
      max: 10,
      step: 0.1,
      disabled: (context) => context['render.showObjectEdges'] === true,
    },
    {
      group: 'viewport_settings',
      label: 'Global Double Sided',
      key: 'render.globalDoubleSided',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Global Material Override',
      key: 'render.globalMaterialOverride',
      type: 'boolean',
      default: false,
    },
    {
      group: 'viewport_settings',
      label: 'Colour',
      key: 'render.materialOverrideColor',
      type: 'color',
      default: '#ffffff',
      disabled: (context) => context['render.globalMaterialOverride'] === true,
    },
  ],
};

export const SETTING_PANEL_TABS = {
  TREE_VIEW: 'tree_view',
  PROJECT_SETTINGS: 'project_settings',
  RENDER_SETTINGS: 'render_settings',
  REPORTS: 'reports',
  SELECTED_OBJECT: 'selected_object',
  SELECTED_MESH: 'selected_mesh',
  BOOKMARKS: 'bookmarks',
  ANNOTATIONS: 'annotations',
  MEASUREMENTS: 'measurements',
  NEW_OBJECTS: 'new_objects',

  VIEWPORT_SETTINGS: 'viewport_settings',
};

export const PANEL_WIDTH = {
  LEFT_MAX: 180,
  RIGHT_MAX: 500,
  LEFT_MIN: 50,
  RIGHT_MIN: 50,
  OBJECT_PARAMETER_PANEL_WIDTH: 175,
};

export const PANEL_ICONS = {
  projector: Projector,
  model: Shapes,
  sphere: Globe,
  cube: Box,
  boxes: Boxes,
  torusknot: Boxes,
  plane: Grid3x3,
  cylinder: Cylinder,
  folder: Folder,
  group: Zap,
  camera_bookmark: Camera,
  measurement: Ruler,
  comment: MessageCircle,
  file: FileUp,
  box: Box,
  ban: Ban,
  led_screen: MonitorPlay,
  tree: ListTree,
  monitor: Monitor,
  chart_column: ChartColumn,
  tag: Tag,
  project: PencilRuler,
};

export const getPanelDefaults = (type) => {
  return Object.fromEntries(
    (settingPanels[type] ?? []).filter((f) => f.key !== undefined).map(({ key, ...rest }) => [key, rest])
  );
};
